/**
 * Theme Service
 *
 * Manages application theme state with:
 * - Persistence via IndexedDB for user preference
 * - System preference detection (prefers-color-scheme)
 * - Event subscription for theme changes
 * - Smooth transitions between themes
 *
 * WHY IndexedDB instead of localStorage:
 * - Asynchronous (doesn't block the main thread)
 * - Larger storage capacity (no 5-10MB limit)
 * - Better suited for modern web applications
 * - Works reliably across privacy modes
 *
 * Usage:
 *   import { themeService } from '@/services/themeService';
 *
 *   // Initialize on app start (required!)
 *   await themeService.initialize();
 *
 *   // Get current theme
 *   const theme = themeService.getTheme();
 *
 *   // Toggle theme
 *   await themeService.toggleTheme();
 *
 *   // Subscribe to changes
 *   const unsubscribe = themeService.subscribe((theme) => {
 *     console.log('Theme changed to:', theme);
 *   });
 */

/* eslint-disable no-undef */
/* Reason: CustomEvent is a standard browser API, allowed in client-side code */

import { themeStorage } from './themeStorage';

/**
 * Available theme modes for user preference
 * - 'auto': Follow system preference (prefers-color-scheme)
 * - 'light': Force light theme
 * - 'dark': Force dark theme
 */
export type ThemeMode = 'auto' | 'light' | 'dark';

/**
 * Theme change callback type
 */
export type ThemeChangeCallback = (theme: ThemeMode) => void;

/**
 * Theme configuration options
 */
export interface ThemeOptions {
  /** Default theme if no preference is stored */
  defaultTheme?: ThemeMode;
  /** Enable smooth transitions when switching themes */
  enableTransitions?: boolean;
  /** Callback fired when theme changes */
  onChange?: ThemeChangeCallback;
}

/**
 * Theme subscriber information
 */
interface Subscriber {
  id: number;
  callback: ThemeChangeCallback;
}

/**
 * Custom event detail type for theme change events
 */
interface ThemeChangeEventDetail {
  theme: ThemeMode;
}

/**
 * Theme Service Class
 *
 * Singleton service for managing application themes.
 * Handles persistence, system preferences, and event distribution.
 */
export class ThemeService {
  /** User's theme preference (can be 'auto', 'light', or 'dark') */
  private userPreference: ThemeMode = 'light';

  /** The actual theme applied to the DOM (always 'light' or 'dark', resolved from 'auto') */
  private actualAppliedTheme: 'light' | 'dark' = 'light';

  /** Subscribers list */
  private subscribers: Subscriber[] = [];

  /** Next subscriber ID */
  private nextSubscriberId = 0;

  /** Service instance (singleton) */
  private static instance: ThemeService;

  /** Transition duration in milliseconds */
  private static readonly TRANSITION_DURATION = 300;

  /** Whether theme service has been initialized */
  private isInitialized = false;

  /**
   * Get the singleton instance
   */
  static getInstance(): ThemeService {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  /**
   * Get the opposite theme mode
   */
  static getOppositeTheme(theme: ThemeMode): ThemeMode {
    return theme === 'light' ? 'dark' : 'light';
  }

  /**
   * Private constructor - use getInstance() instead
   */
  private constructor() {
    // Intentionally empty - initialization happens in initialize()
  }

  /**
   * Get current theme
   */
  getTheme(): ThemeMode {
    return this.userPreference;
  }

  /**
   * Get the actual theme applied to DOM (always 'light' or 'dark')
   */
  getActualTheme(): 'light' | 'dark' {
    return this.actualAppliedTheme;
  }

  /**
   * Check if current theme is dark
   */
  isDark(): boolean {
    return this.actualAppliedTheme === 'dark';
  }

  /**
   * Check if current theme is light
   */
  isLight(): boolean {
    return this.actualAppliedTheme === 'light';
  }

  /**
   * Get current theme as a CSS class name
   */
  getThemeClass(): string {
    return `theme-${this.actualAppliedTheme}`;
  }

  /**
   * Get current theme as HTML data attribute value
   */
  getThemeDataAttr(): string {
    return this.actualAppliedTheme;
  }

  /**
   * Set the theme
   *
   * @param theme - Theme mode to set ('auto', 'light' or 'dark')
   * @returns Promise<void>
   */
  async setTheme(theme: ThemeMode): Promise<void> {
    // Store user preference (can be 'auto', 'light', or 'dark')
    this.userPreference = theme;

    // Resolve 'auto' to actual theme for application
    const actualTheme = this.resolveThemeToActual(theme);

    if (actualTheme === this.actualAppliedTheme) {
      return;
    }

    this.actualAppliedTheme = actualTheme;

    // Apply theme to DOM (always light or dark)
    this.applyTheme(actualTheme);

    // Persist preference to IndexedDB (preserve 'auto' if that was the preference)
    await this.persistTheme(theme);

    // Notify subscribers with the actual applied theme
    this.notifySubscribers(actualTheme);

    // Dispatch custom event for external listeners
    this.dispatchThemeEvent(actualTheme);
  }

  /**
   * Resolve user preference to actual theme (light/dark)
   * - 'auto' -> follows system preference
   * - 'light' or 'dark' -> use directly
   */
  private resolveThemeToActual(theme: ThemeMode): 'light' | 'dark' {
    if (theme === 'auto') {
      return this.getSystemPreference() ?? 'light';
    }
    return theme;
  }

  /**
   * Toggle between light and dark themes
   *
   * @returns Promise<void>
   */
  async toggleTheme(): Promise<void> {
    const newTheme = ThemeService.getOppositeTheme(this.actualAppliedTheme);
    await this.setTheme(newTheme);
  }

  /**
   * Reload theme from storage (API or system preference)
   *
   * Use this after authentication state changes (login/logout)
   * to ensure the theme matches the new state:
   * - After logout: Should switch to 'auto' (system preference)
   * - After login: Should load user's saved theme from API
   *
   * @returns Promise<ThemeMode> - The reloaded theme
   */
  async reloadTheme(): Promise<ThemeMode> {
    const newPreference = await this.determineTheme('auto');

    // Store new preference
    this.userPreference = newPreference;

    // Resolve to actual theme
    const actualTheme = this.resolveThemeToActual(newPreference);

    // Apply if different
    if (actualTheme !== this.actualAppliedTheme) {
      this.actualAppliedTheme = actualTheme;
      this.applyTheme(actualTheme);

      // Notify subscribers
      this.notifySubscribers(actualTheme);
      this.dispatchThemeEvent(actualTheme);
    }

    return newPreference;
  }

  /**
   * Initialize the theme service
   *
   * Reads stored preference from IndexedDB or system preference,
   * then applies the appropriate theme.
   *
   * This method must be called before using the themeService!
   *
   * @param options - Theme configuration options
   * @returns Promise<ThemeMode> - The applied theme
   */
  async initialize(options?: ThemeOptions): Promise<ThemeMode> {
    if (this.isInitialized) {
      // Already initialized - skip redundant initialization
      return this.userPreference;
    }

    const { defaultTheme = 'light', onChange } = options ?? {};

    // Subscribe if callback provided
    if (onChange) {
      this.subscribe(onChange);
    }

    // Determine initial theme preference (async - reads from IndexedDB/API)
    const userPreference = await this.determineTheme(defaultTheme);

    // Store user preference (can be 'auto', 'light', or 'dark')
    this.userPreference = userPreference;

    // Resolve to actual theme for DOM application
    const actualTheme = this.resolveThemeToActual(userPreference);

    // Apply the actual theme (always light or dark) to DOM
    this.actualAppliedTheme = actualTheme;
    this.applyTheme(actualTheme);

    // Set up system preference listener
    this.setupSystemPreferenceListener();

    // Mark as initialized
    this.isInitialized = true;

    return userPreference;
  }

  /**
   * Subscribe to theme changes
   *
   * @param callback - Function to call when theme changes
   * @returns Unsubscribe function
   */
  subscribe(callback: ThemeChangeCallback): () => void {
    const id = this.nextSubscriberId++;
    const subscriber: Subscriber = { id, callback };

    this.subscribers.push(subscriber);

    // Return unsubscribe function
    return () => {
      const index = this.subscribers.findIndex((s) => s.id === id);
      if (index !== -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe once - callback is called only on next change
   *
   * @param callback - Function to call on next theme change
   */
  subscribeOnce(callback: ThemeChangeCallback): void {
    const wrappedCallback = (theme: ThemeMode) => {
      this.unsubscribeByCallback(wrappedCallback);
      callback(theme);
    };

    this.subscribers.push({
      id: this.nextSubscriberId++,
      callback: wrappedCallback,
    });
  }

  /**
   * Determine the theme to use based on authentication status, API, and IndexedDB
   *
   * STRATEGY (KISS):
   * - If user is authenticated: Load from API (database) → System preference
   * - If user is not authenticated: Force 'auto' mode (follow system preference)
   *
   * Simplified version: No IndexedDB for non-authenticated users.
   * Login/Register pages automatically use system preference (auto mode).
   *
   * @param defaultTheme - Default theme to use if no preference is stored (default: 'auto' for login/register)
   * @returns Promise<ThemeMode> - Determined theme mode
   */
  private async determineTheme(defaultTheme: ThemeMode): Promise<ThemeMode> {
    // Try to load from API if user is authenticated
    try {
      const { authClient } = await import('../lib/auth');
      const session = await authClient.getSession();

      // Check if session exists and user is authenticated
      const sessionData = session as unknown as {
        data?: { user?: { id?: string } };
        user?: { id?: string };
      };

      const userId = sessionData?.data?.user?.id ?? sessionData?.user?.id;

      if (userId) {
        // User is authenticated - load theme from backend API
        const { client } = await import('../lib/rpc-client');
        const response = await client.api['users']['me'].preferences.$get();

        if (response.ok) {
          const data = await response.json();
          if (data.theme && typeof data.theme === 'string') {
            return data.theme;
          }
        }
      }
    } catch {
      // Auth check failed - user is not authenticated
      // This is expected on non-auth routes (login/register)
    }

    // Non-authenticated users (login/register): Force 'auto' mode by default
    // This makes Login/Register pages automatically follow system preference
    // Much simpler than storing IndexedDB for temporary users
    // Use defaultTheme parameter if needed (currently always 'auto')
    return defaultTheme || 'auto';
  }

  /**
   * Get theme from IndexedDB
   *
   * @returns Promise<ThemeMode | null> - stored theme or null if not set
   */
  private async getStoredTheme(): Promise<ThemeMode | null> {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const theme = await themeStorage.getTheme();
      return theme;
    } catch {
      // IndexedDB access failed - proceed with system preference
      return null;
    }
  }

  /**
   * Persist theme to storage
   *
   * STRATEGY (KISS):
   * - If user is authenticated: Save to API (database) + IndexedDB (cache for instant load)
   * - If user is not authenticated: Do nothing (Login/Register pages use 'auto' mode only)
   *
   * Simplified version: IndexedDB is only used as a cache for authenticated users,
   * not for storing preferences of non-authenticated users.
   *
   * @param theme - Theme mode to persist
   */
  private async persistTheme(theme: ThemeMode): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // First, check if user is authenticated
      try {
        const { authClient } = await import('../lib/auth');
        const session = await authClient.getSession();

        // Check if session exists and user is authenticated
        const sessionData = session as unknown as {
          data?: { user?: { id?: string } };
          user?: { id?: string };
        };

        const userId = sessionData?.data?.user?.id ?? sessionData?.user?.id;

        if (userId) {
          // User is authenticated - sync to API AND IndexedDB (as cache)

          // 1. Save to IndexedDB first (instant cache)
          await themeStorage.setTheme(theme);

          // 2. Sync to backend API (database - source of truth)
          const { client } = await import('../lib/rpc-client');
          await client.api['users']['me'].preferences.$patch({
            json: { theme },
          });
        }
        // If not authenticated, do nothing (Login/Register pages use 'auto' mode)
      } catch {
        // Auth check failed - user is on login/register
        // Do not store anything, theme follows system preference automatically
      }
    } catch {
      // Storage completely failed - theme change still works visually
    }
  }

  /**
   * Get system color scheme preference
   * Returns 'light' or 'dark' based on prefers-color-scheme, or null if unavailable
   */
  private getSystemPreference(): 'light' | 'dark' | null {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return null;
    }

    try {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

      return prefersDark ? 'dark' : 'light';
    } catch {
      return null;
    }
  }

  /**
   * Set up listener for system preference changes
   */
  private setupSystemPreferenceListener(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = async (): Promise<void> => {
        // Only auto-switch if user has selected 'auto' mode
        const storedPreference = await this.getStoredTheme();

        if (storedPreference === 'auto') {
          const newTheme = this.getSystemPreference() ?? 'light';

          // Only change if different (prevents loops)
          if (newTheme !== this.actualAppliedTheme) {
            await this.setTheme(newTheme);
          }
        }
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      }
      // Older browsers (Safari < 14)
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
      }
    } catch {
      // matchMedia not fully supported - skip
    }
  }

  /**
   * Apply theme to the DOM
   */
  private applyTheme(theme: ThemeMode): void {
    if (typeof document === 'undefined') {
      return;
    }

    const { documentElement: html, body } = document;

    // Apply via data attribute (for reference)
    html.setAttribute('data-theme', theme);

    // Apply Tailwind v4 dark mode - use "dark" class for dark theme
    if (theme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    // Legacy: Also apply via body class for backward compatibility
    body.classList.remove('theme-light', 'theme-dark');
    body.classList.add(`theme-${theme}`);

    // Enable transitions (optional)
    this.enableTransitions();
  }

  /**
   * Enable smooth transitions for theme change
   */
  private enableTransitions(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const { body } = document;

    // Add transition style
    body.style.transition = 'background-color 0.3s ease, color 0.3s ease';

    // Remove after transition completes
    setTimeout(() => {
      body.style.transition = '';
    }, ThemeService.TRANSITION_DURATION);
  }

  /**
   * Notify all subscribers of theme change
   */
  private notifySubscribers(theme: ThemeMode): void {
    this.subscribers.forEach((subscriber) => {
      try {
        subscriber.callback(theme);
      } catch {
        // Subscriber callback failed - continue with others
      }
    });
  }

  /**
   * Dispatch custom event for external listeners
   */
  private dispatchThemeEvent(theme: ThemeMode): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const event = new CustomEvent('themechange', {
        detail: { theme } as ThemeChangeEventDetail,
        bubbles: true,
      });

      window.dispatchEvent(event);
    } catch {
      // CustomEvent not supported or failed
    }
  }

  /**
   * Unsubscribe a specific callback
   */
  private unsubscribeByCallback(callback: ThemeChangeCallback): void {
    const index = this.subscribers.findIndex((s) => s.callback === callback);

    if (index !== -1) {
      this.subscribers.splice(index, 1);
    }
  }
}

/**
 * Singleton export for easy usage
 */
export const themeService = ThemeService.getInstance();

/**
 * React hook factory for using theme in components
 *
 * Usage in React component:
 *   const { theme, toggleTheme, setTheme } = useTheme();
 *
 * NOTE: toggleTheme and setTheme are now async (IndexedDB storage)
 */
export function createUseTheme() {
  return function useTheme(): {
    theme: ThemeMode;
    toggleTheme: () => Promise<void>;
    setTheme: (theme: ThemeMode) => Promise<void>;
    isDark: boolean;
  } {
    // This is a placeholder - actual React hooks would need React import
    // Use the service directly in components for now
    return {
      theme: themeService.getTheme(),
      toggleTheme: () => themeService.toggleTheme(),
      setTheme: (t: ThemeMode) => themeService.setTheme(t),
      isDark: themeService.isDark(),
    };
  };
}
